import { DOCUMENT } from '@angular/common';
import {
    AfterViewInit, Directive, ElementRef, Inject, Input, OnDestroy, Renderer2,
} from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { fromEvent, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, pairwise, share, takeUntil, debounceTime } from 'rxjs/operators';

import { HideOnScrollService } from './hide-on-scroll.service';

import { CommonWindow } from '@core/domain/models/common-window.model';
import { CorePlatformService } from '@core/infrastructure/platform/platform.service';

enum VisibilityState {
    visible = 'is-show',
    hidden = 'is-hidden',
}

enum Direction {
    Up = 'Up',
    Down = 'Down',
}
@Directive({
    selector: '[hideOnScroll]',
    standalone: true,
    providers: [HideOnScrollService],
})
export class HideOnScrollDirective implements AfterViewInit, OnDestroy {
    @Input() public showIfNoScrollDelay: boolean;

    @Input() public lastPageYOffset = 100;

    private readonly destroySubject = new Subject<void>();

    private isVisible = true;

    private domEl: ElementRef;

    private readonly bodyEl: HTMLBodyElement;

    constructor(
        private readonly renderer: Renderer2,
        private readonly elementRef: ElementRef,
        private readonly hideOnScrollService: HideOnScrollService,
        @Inject(WINDOW) private readonly window: CommonWindow,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly platformService: CorePlatformService
    ) {
        if (this.platformService.isBrowser()) {
            this.bodyEl = this.window.document.querySelector('body');
        }
    }

    public ngAfterViewInit(): void {
        this.domEl = this.elementRef.nativeElement;
        this.listenScrollBar();
    }

    private listenScrollBar(): void {
        if (this.platformService.isBrowser()) {
            const scroll$ = fromEvent(this.window, 'scroll').pipe(
                map(() => this.window.scrollY),
                pairwise(),
                map(([y1, y2]): {
                    lastPageYOffset: number;
                    direction: Direction;
                } => {
                    return {
                        lastPageYOffset: y2,
                        direction: y2 < y1 ? Direction.Up : Direction.Down,
                    };
                }),
                distinctUntilChanged(),
                share(),
            );

            if (this.showIfNoScrollDelay) {
                fromEvent(this.window, 'scroll')
                    .pipe(debounceTime(2000))
                    .subscribe(() => {
                        this.handleNoScroll();
                    });
            }

            const scrollUp$ = scroll$.pipe(
                filter((direction) => {
                    const scrollTop = this.document.documentElement.scrollTop || this.document.body.scrollTop || 0;
                    const scrollHeight = this.document.documentElement.scrollHeight;
                    const clientHeight = this.document.documentElement.clientHeight;

                    const distanceToBottom = scrollHeight - (scrollTop + clientHeight);

                    return direction.direction === Direction.Up && distanceToBottom >= this.lastPageYOffset;
                }),
                takeUntil(this.destroySubject),
            );

            const scrollDown$ = scroll$.pipe(
                filter((direction) =>
                    direction.lastPageYOffset >= this.lastPageYOffset
                    && direction.direction === Direction.Down),
                takeUntil(this.destroySubject),
            );

            scrollUp$.subscribe(() => {
                this.setCssforContainer(true);
            });

            scrollDown$.subscribe(() => {
                this.setCssforContainer(false);
            });
        }
    }

    private handleNoScroll() {
        if (!this.isVisible) {
            this.setCssforContainer(true);
        }
    }

    private setCssforContainer(addClass: boolean) {
        // eslint-disable-next-line rxjs/no-subject-value
        if (!this.hideOnScrollService.hideOnDontListenSubject.value) {
            this.removeClass();
            this.isVisible = addClass;
            this.addClass();
        } else {
            this.hideOnScrollService.hideOnDontListenSubject.next(false);
        }
    }

    private addClass() {
        this.renderer.addClass(this.domEl, this.visibilityState);
    }

    private removeClass() {
        this.renderer.removeClass(this.domEl, this.visibilityState);
    }

    public ngOnDestroy() {
        this.destroySubject.next();
        this.destroySubject.complete();
    }

    private get visibilityState(): VisibilityState {
        return this.isVisible ? VisibilityState.visible : VisibilityState.hidden;
    }
}
